import React, { useState, useEffect, useCallback, useRef } from "react";
import { getData } from "../../../Services/Fetchnodeservices";

// ─────────────────────────────────────────────────────────────────────────────
// ICONS
// ─────────────────────────────────────────────────────────────────────────────
const PlusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const UploadIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

const PriorityHighIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

// ─────────────────────────────────────────────────────────────────────────────
// API FUNCTIONS (to be imported from your API service)
// ─────────────────────────────────────────────────────────────────────────────
// import { getdata, postdata, putdata, deletedata } from "../../services/api";

// For now, using mock functions - replace with actual imports
const getdata = async (endpoint) => {
  // Replace with actual API call
  console.log("GET:", endpoint);
  return { data: [] };
};

const postdata = async (endpoint, data) => {
  console.log("POST:", endpoint, data);
  return { data: { id: Date.now(), ...data } };
};

const putdata = async (endpoint, data) => {
  console.log("PUT:", endpoint, data);
  return { data };
};

// ─────────────────────────────────────────────────────────────────────────────
// ADD FEEDBACK DIALOG COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const AddFeedbackDialog = ({ isDark, onClose, onSave, editingFeedback }) => {
  const [formData, setFormData] = useState({
    title: editingFeedback?.title || "",
    platform: editingFeedback?.platform || "other",
    category: editingFeedback?.category || "bug",
    priority: editingFeedback?.priority || "medium",
    points: editingFeedback?.points || [{ description: "", images: [] }],
    dev_notes: editingFeedback?.dev_notes || "",
  });
  const [uploadedImages, setUploadedImages] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePointChange = (index, value) => {
    const newPoints = [...formData.points];
    newPoints[index] = { ...newPoints[index], description: value };
    setFormData(prev => ({ ...prev, points: newPoints }));
  };

  const addPoint = () => {
    setFormData(prev => ({
      ...prev,
      points: [...prev.points, { description: "", images: [] }]
    }));
  };

  const removePoint = (index) => {
    const newPoints = formData.points.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, points: newPoints }));
  };

  const handleImageUpload = async (files) => {
    // Implement image upload to server
    // For now, just store local preview
    const newImages = [];
    for (let file of files) {
      const reader = new FileReader();
      const imageUrl = await new Promise((resolve) => {
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
      newImages.push(imageUrl);
    }
    setUploadedImages(prev => [...prev, ...newImages]);
  };

  const handleFileInput = (e) => {
    const files = Array.from(e.target.files);
    handleImageUpload(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    handleImageUpload(files);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.title.trim()) return;
    
    onSave({
      title: formData.title,
      platform: formData.platform,
      category: formData.category,
      priority: formData.priority,
      points: JSON.stringify(formData.points.filter(p => p.description.trim())),
      dev_notes: formData.dev_notes,
      status: editingFeedback?.status || "pending",
    });
    onClose();
  };

  const dialogStyles = {
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.7)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
      backdropFilter: "blur(4px)",
    },
    dialog: {
      width: "90%",
      maxWidth: "580px",
      background: isDark ? "#141824" : "#ffffff",
      borderRadius: "20px",
      boxShadow: "0 25px 40px -12px rgba(0,0,0,0.4)",
      overflow: "hidden",
      animation: "fadeIn 0.2s ease-out",
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "18px 24px",
      borderBottom: isDark ? "1px solid #1e2740" : "1px solid #e2e8f0",
    },
    title: {
      fontSize: "20px",
      fontWeight: 700,
      color: isDark ? "#f1f5f9" : "#0f172a",
      margin: 0,
    },
    closeBtn: {
      background: "none",
      border: "none",
      cursor: "pointer",
      color: isDark ? "#94a3b8" : "#64748b",
      padding: "4px",
      display: "flex",
      borderRadius: "8px",
    },
    body: {
      padding: "24px",
      maxHeight: "65vh",
      overflowY: "auto",
    },
    formGroup: {
      marginBottom: "20px",
    },
    label: {
      display: "block",
      marginBottom: "8px",
      fontSize: "13px",
      fontWeight: 600,
      color: isDark ? "#cbd5e1" : "#334155",
    },
    required: {
      color: "#ef4444",
      marginLeft: "4px",
    },
    input: {
      width: "100%",
      padding: "12px 14px",
      background: isDark ? "#0d1117" : "#f8fafc",
      border: isDark ? "1px solid #1e2740" : "1px solid #e2e8f0",
      borderRadius: "12px",
      fontSize: "14px",
      color: isDark ? "#f1f5f9" : "#1e293b",
      outline: "none",
      boxSizing: "border-box",
    },
    textarea: {
      width: "100%",
      padding: "12px 14px",
      background: isDark ? "#0d1117" : "#f8fafc",
      border: isDark ? "1px solid #1e2740" : "1px solid #e2e8f0",
      borderRadius: "12px",
      fontSize: "14px",
      color: isDark ? "#f1f5f9" : "#1e293b",
      outline: "none",
      fontFamily: "inherit",
      resize: "vertical",
      minHeight: "80px",
      boxSizing: "border-box",
    },
    select: {
      width: "100%",
      padding: "12px 14px",
      background: isDark ? "#0d1117" : "#f8fafc",
      border: isDark ? "1px solid #1e2740" : "1px solid #e2e8f0",
      borderRadius: "12px",
      fontSize: "14px",
      color: isDark ? "#f1f5f9" : "#1e293b",
      outline: "none",
      cursor: "pointer",
    },
    rowTwoColumns: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "16px",
    },
    pointsItem: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      marginBottom: "10px",
    },
    pointsNumber: {
      width: "28px",
      height: "28px",
      borderRadius: "6px",
      background: isDark ? "#1e2740" : "#f1f5f9",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "12px",
      fontWeight: 600,
      color: isDark ? "#94a3b8" : "#64748b",
      flexShrink: 0,
    },
    pointsInput: {
      flex: 1,
      padding: "8px 12px",
      background: isDark ? "#0d1117" : "#f8fafc",
      border: isDark ? "1px solid #1e2740" : "1px solid #e2e8f0",
      borderRadius: "8px",
      fontSize: "13px",
      color: isDark ? "#f1f5f9" : "#1e293b",
      outline: "none",
    },
    removePointBtn: {
      background: "none",
      border: "none",
      cursor: "pointer",
      padding: "4px",
      color: "#ef4444",
      display: "flex",
    },
    addPointBtn: {
      display: "flex",
      alignItems: "center",
      gap: "6px",
      padding: "6px 12px",
      background: "transparent",
      border: isDark ? "1px solid #3b82f6" : "1px solid #3b82f6",
      borderRadius: "8px",
      fontSize: "12px",
      fontWeight: 500,
      color: "#3b82f6",
      cursor: "pointer",
      marginTop: "8px",
    },
    footer: {
      display: "flex",
      justifyContent: "flex-end",
      gap: "12px",
      padding: "16px 24px",
      borderTop: isDark ? "1px solid #1e2740" : "1px solid #e2e8f0",
      background: isDark ? "#0f1520" : "#fafcff",
    },
    cancelBtn: {
      padding: "10px 20px",
      background: "transparent",
      border: isDark ? "1px solid #2a3a5a" : "1px solid #cbd5e1",
      borderRadius: "10px",
      fontSize: "14px",
      fontWeight: 500,
      color: isDark ? "#94a3b8" : "#475569",
      cursor: "pointer",
    },
    saveBtn: {
      padding: "10px 24px",
      background: "#4a6cf7",
      border: "none",
      borderRadius: "10px",
      fontSize: "14px",
      fontWeight: 600,
      color: "#fff",
      cursor: "pointer",
    },
  };

  return (
    <div style={dialogStyles.overlay} onClick={onClose}>
      <div style={dialogStyles.dialog} onClick={(e) => e.stopPropagation()}>
        <div style={dialogStyles.header}>
          <h3 style={dialogStyles.title}>{editingFeedback ? "Edit Feedback" : "New Feedback"}</h3>
          <button style={dialogStyles.closeBtn} onClick={onClose}>
            <CloseIcon />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={dialogStyles.body}>
            <div style={dialogStyles.formGroup}>
              <label style={dialogStyles.label}>Title <span style={dialogStyles.required}>*</span></label>
              <input
                type="text"
                name="title"
                style={dialogStyles.input}
                placeholder="e.g. Add filter option in admin panel"
                value={formData.title}
                onChange={handleChange}
                required
                autoFocus
              />
            </div>

            <div style={dialogStyles.rowTwoColumns}>
              <div style={dialogStyles.formGroup}>
                <label style={dialogStyles.label}>Platform *</label>
                <select name="platform" style={dialogStyles.select} value={formData.platform} onChange={handleChange} required>
                  <option value="user_app">User App</option>
                  <option value="vendor_app">Vendor App</option>
                  <option value="rider_app">Rider App</option>
                  <option value="website">Website</option>
                  <option value="admin_panel">Admin Panel</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div style={dialogStyles.formGroup}>
                <label style={dialogStyles.label}>Category *</label>
                <select name="category" style={dialogStyles.select} value={formData.category} onChange={handleChange} required>
                  <option value="bug">Bug</option>
                  <option value="feature">Feature</option>
                  <option value="ui_fix">UI Fix</option>
                  <option value="change">Change</option>
                </select>
              </div>
            </div>

            <div style={dialogStyles.rowTwoColumns}>
              <div style={dialogStyles.formGroup}>
                <label style={dialogStyles.label}>Priority *</label>
                <select name="priority" style={dialogStyles.select} value={formData.priority} onChange={handleChange} required>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div style={dialogStyles.formGroup}>
                <label style={dialogStyles.label}>Dev Notes</label>
                <textarea
                  name="dev_notes"
                  style={dialogStyles.textarea}
                  placeholder="Notes for developer..."
                  value={formData.dev_notes}
                  onChange={handleChange}
                  rows="2"
                />
              </div>
            </div>

            <div style={dialogStyles.formGroup}>
              <label style={dialogStyles.label}>Points / Description</label>
              {formData.points.map((point, index) => (
                <div key={index} style={dialogStyles.pointsItem}>
                  <div style={dialogStyles.pointsNumber}>{index + 1}</div>
                  <input
                    type="text"
                    style={dialogStyles.pointsInput}
                    placeholder="Describe the issue or feature..."
                    value={point.description}
                    onChange={(e) => handlePointChange(index, e.target.value)}
                  />
                  {formData.points.length > 1 && (
                    <button type="button" style={dialogStyles.removePointBtn} onClick={() => removePoint(index)}>
                      <TrashIcon />
                    </button>
                  )}
                </div>
              ))}
              <button type="button" style={dialogStyles.addPointBtn} onClick={addPoint}>
                <PlusIcon /> Add Point
              </button>
            </div>
          </div>
          <div style={dialogStyles.footer}>
            <button type="button" style={dialogStyles.cancelBtn} onClick={onClose}>Cancel</button>
            <button type="submit" style={dialogStyles.saveBtn}>{editingFeedback ? "Update" : "Submit"}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────
const StatusBadge = ({ status, isDark }) => {
  const config = {
    pending: { bg: isDark ? "rgba(245,158,11,0.15)" : "rgba(245,158,11,0.1)", color: "#f59e0b" },
    in_progress: { bg: isDark ? "rgba(59,130,246,0.15)" : "rgba(59,130,246,0.1)", color: "#3b82f6" },
    completed: { bg: isDark ? "rgba(34,197,94,0.15)" : "rgba(34,197,94,0.1)", color: "#22c55e" },
  };
  const c = config[status] || config.pending;
  return <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: "20px", fontSize: "10px", fontWeight: 600, background: c.bg, color: c.color }}>{status?.replace('_', ' ')}</span>;
};

const TypeBadge = ({ category, isDark }) => {
  const config = {
    feature: { bg: isDark ? "rgba(139,92,246,0.15)" : "rgba(139,92,246,0.1)", color: "#8b5cf6" },
    bug: { bg: isDark ? "rgba(239,68,68,0.15)" : "rgba(239,68,68,0.1)", color: "#ef4444" },
    change: { bg: isDark ? "rgba(59,130,246,0.15)" : "rgba(59,130,246,0.1)", color: "#3b82f6" },
    ui_fix: { bg: isDark ? "rgba(16,185,129,0.15)" : "rgba(16,185,129,0.1)", color: "#10b981" },
  };
  const c = config[category] || config.feature;
  return <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: "12px", fontSize: "10px", fontWeight: 500, background: c.bg, color: c.color }}>{category}</span>;
};

const PriorityBadge = ({ priority, isDark }) => {
  const config = {
    urgent: { bg: isDark ? "rgba(239,68,68,0.15)" : "rgba(239,68,68,0.1)", color: "#ef4444" },
    high: { bg: isDark ? "rgba(239,68,68,0.12)" : "rgba(239,68,68,0.08)", color: "#f97316" },
    medium: { bg: isDark ? "rgba(245,158,11,0.15)" : "rgba(245,158,11,0.1)", color: "#f59e0b" },
    low: { bg: isDark ? "rgba(148,163,184,0.15)" : "rgba(148,163,184,0.1)", color: "#94a3b8" },
  };
  const c = config[priority] || config.medium;
  return <span style={{ fontSize: "10px", fontWeight: 600, color: c.color, display: "flex", alignItems: "center", gap: "3px" }}>
    {priority === "urgent" && <PriorityHighIcon />} {priority}
  </span>;
};

const StatCard = ({ title, count, isDark }) => (
  <div style={{
    background: isDark ? "#141824" : "#ffffff",
    border: isDark ? "1px solid #1e2740" : "1px solid #e2e8f0",
    borderRadius: "12px", padding: "12px 20px", textAlign: "center", flex: 1, minWidth: "80px",
  }}>
    <div style={{ fontSize: "24px", fontWeight: 700, color: isDark ? "#f1f5f9" : "#1e293b" }}>{count}</div>
    <div style={{ fontSize: "11px", fontWeight: 500, color: isDark ? "#64748b" : "#94a3b8", marginTop: "4px" }}>{title}</div>
  </div>
);

const TabButton = ({ label, isActive, onClick, isDark }) => (
  <button onClick={onClick} style={{
    padding: "6px 14px", borderRadius: "20px", fontSize: "12px", fontWeight: 500,
    background: isActive ? "#3b82f6" : isDark ? "transparent" : "#f1f5f9",
    color: isActive ? "#fff" : isDark ? "#94a3b8" : "#475569",
    border: isActive ? "1px solid #3b82f6" : isDark ? "1px solid #1e2740" : "1px solid #e2e8f0",
    cursor: "pointer",
  }}>{label}</button>
);

const FeedbackCard = ({ feedback, isDark, onStatusChange }) => {
  const [expanded, setExpanded] = useState(false);
  
  // Parse points JSON if it's a string
  let pointsData = [];
  try {
    pointsData = typeof feedback.points === 'string' ? JSON.parse(feedback.points) : (feedback.points || []);
  } catch (e) {
    pointsData = [{ description: feedback.points || "" }];
  }

  return (
    <div style={{
      background: isDark ? "#141824" : "#ffffff",
      border: isDark ? "1px solid #1e2740" : "1px solid #e2e8f0",
      borderRadius: "12px", padding: "16px", marginBottom: "12px",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
          <span style={{ fontSize: "13px", fontWeight: 700, color: "#3b82f6" }}>#{feedback.id}</span>
          <span style={{ fontSize: "14px", fontWeight: 600, color: isDark ? "#f1f5f9" : "#1e293b" }}>{feedback.title}</span>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          <select value={feedback.status} onChange={(e) => onStatusChange(feedback.id, e.target.value)} style={{
            padding: "4px 8px", borderRadius: "6px", fontSize: "11px",
            background: isDark ? "#0f1520" : "#f8fafc",
            border: isDark ? "1px solid #1e2740" : "1px solid #e2e8f0",
            color: isDark ? "#e2e8f0" : "#1e293b", cursor: "pointer",
          }}>
            <option value="pending">Pending</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
          </select>
          <button onClick={() => setExpanded(!expanded)} style={{ background: "none", border: "none", cursor: "pointer", color: isDark ? "#64748b" : "#94a3b8" }}>
            {expanded ? "▲" : "▼"}
          </button>
        </div>
      </div>

      <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "8px" }}>
        <TypeBadge category={feedback.category} isDark={isDark} />
        <PriorityBadge priority={feedback.priority} isDark={isDark} />
        <span style={{ fontSize: "11px", color: isDark ? "#64748b" : "#94a3b8" }}>{feedback.platform?.replace('_', ' ')}</span>
      </div>

      <div style={{ fontSize: "11px", color: isDark ? "#64748b" : "#94a3b8", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
        <span>by {feedback.created_by_name}</span>
        <span>•</span>
        <span>{new Date(feedback.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
        {feedback.status !== "completed" && (
          <>
            <span>•</span>
            <StatusBadge status={feedback.status} isDark={isDark} />
          </>
        )}
        {feedback.completed_by_name && (
          <>
            <span>•</span>
            <span>Completed by {feedback.completed_by_name}</span>
          </>
        )}
      </div>

      {expanded && (
        <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: isDark ? "1px solid #1e2740" : "1px solid #e2e8f0" }}>
          <div style={{ fontSize: "12px", color: isDark ? "#94a3b8" : "#64748b" }}>
            <strong>Points:</strong>
            {pointsData.map((point, idx) => (
              <div key={idx} style={{ marginTop: "6px", marginLeft: "8px" }}>• {point.description}</div>
            ))}
            {feedback.dev_notes && (
              <>
                <div style={{ marginTop: "12px" }}><strong>Dev Notes:</strong></div>
                <div style={{ marginTop: "6px" }}>{feedback.dev_notes}</div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const DevFeedback = ({ isDark = true }) => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState("All");
  const [sortBy, setSortBy] = useState("priority");
  const [sortOrder, setSortOrder] = useState("desc");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingFeedback, setEditingFeedback] = useState(null);

  const platforms = ["All", "user_app", "vendor_app", "rider_app", "website", "admin_panel", "other"];

  // Fetch feedbacks from API
  const fetchFeedbacks = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getData("devfeedback/DevFeedback");
      console.log(response,"dfcgvhjbknlm;");
      
      // Filter out hidden items (hide: 0 means not hidden, hide: 1 means hidden)
      const visibleFeedbacks = (response.data || []).filter(f => f.hide === 0);
      setFeedbacks(visibleFeedbacks);
    } catch (error) {
      console.error("Error fetching feedbacks:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFeedbacks();
  }, [fetchFeedbacks]);

  // Stats calculation
  const stats = {
    pending: feedbacks.filter(f => f.status === "pending").length,
    inProgress: feedbacks.filter(f => f.status === "in_progress").length,
    completed: feedbacks.filter(f => f.status === "completed").length,
    total: feedbacks.length,
  };

  // Filter by platform
  let filteredFeedbacks = selectedTab === "All"
    ? feedbacks
    : feedbacks.filter(f => f.platform === selectedTab);

  // Sort
  filteredFeedbacks = [...filteredFeedbacks].sort((a, b) => {
    if (sortBy === "priority") {
      const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };
      return sortOrder === "asc"
        ? priorityOrder[a.priority] - priorityOrder[b.priority]
        : priorityOrder[b.priority] - priorityOrder[a.priority];
    } else if (sortBy === "id") {
      return sortOrder === "asc" ? a.id - b.id : b.id - a.id;
    } else if (sortBy === "date") {
      return sortOrder === "asc"
        ? new Date(a.created_at) - new Date(b.created_at)
        : new Date(b.created_at) - new Date(a.created_at);
    }
    return 0;
  });

  const handleStatusChange = async (id, newStatus) => {
    try {
      await putdata(`devfeedback/DevFeedback/${id}`, { status: newStatus });
      setFeedbacks(prev => prev.map(f => f.id === id ? { ...f, status: newStatus } : f));
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  const handleAddFeedback = () => {
    setEditingFeedback(null);
    setIsDialogOpen(true);
  };

  const handleSaveFeedback = async (feedbackData) => {
    try {
      if (editingFeedback) {
        await putdata(`devfeedback/DevFeedback/${editingFeedback.id}`, feedbackData);
        await fetchFeedbacks();
      } else {
        await postdata("devfeedback/DevFeedback", feedbackData);
        await fetchFeedbacks();
      }
      setIsDialogOpen(false);
      setEditingFeedback(null);
    } catch (error) {
      console.error("Error saving feedback:", error);
    }
  };

  const toggleSortOrder = () => {
    setSortOrder(prev => prev === "asc" ? "desc" : "asc");
  };

  const styles = {
    container: {
      background: "transparent",
      fontFamily: "'Segoe UI', 'DM Sans', sans-serif",
      padding: "20px 24px",
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "20px",
      flexWrap: "wrap",
      gap: "16px",
    },
    title: {
      fontSize: "20px",
      fontWeight: 700,
      color: isDark ? "#f1f5f9" : "#1e293b",
      margin: 0,
    },
    addBtn: {
      display: "flex",
      alignItems: "center",
      gap: "6px",
      padding: "8px 18px",
      background: "#4a6cf7",
      color: "#fff",
      border: "none",
      borderRadius: "10px",
      fontSize: "13px",
      fontWeight: 600,
      cursor: "pointer",
    },
    statsRow: {
      display: "flex",
      gap: "12px",
      marginBottom: "24px",
      flexWrap: "wrap",
    },
    tabsRow: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: "20px",
      gap: "16px",
    },
    tabs: {
      display: "flex",
      gap: "8px",
      flexWrap: "wrap",
    },
    sortSection: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    sortLabel: {
      fontSize: "12px",
      fontWeight: 500,
      color: isDark ? "#64748b" : "#475569",
    },
    sortButtons: {
      display: "flex",
      gap: "6px",
    },
    sortBtn: (isActive) => ({
      padding: "5px 12px",
      borderRadius: "20px",
      fontSize: "12px",
      fontWeight: 500,
      background: isActive ? "#3b82f6" : isDark ? "transparent" : "#f1f5f9",
      color: isActive ? "#fff" : isDark ? "#94a3b8" : "#475569",
      border: isActive ? "1px solid #3b82f6" : isDark ? "1px solid #1e2740" : "1px solid #e2e8f0",
      cursor: "pointer",
    }),
    orderBtn: {
      background: "none",
      border: "none",
      cursor: "pointer",
      color: isDark ? "#64748b" : "#94a3b8",
      fontSize: "12px",
      padding: "5px 8px",
      borderRadius: "6px",
    },
    feedbackList: {
      marginTop: "20px",
    },
    emptyState: {
      textAlign: "center",
      padding: "60px",
      color: isDark ? "#64748b" : "#94a3b8",
      fontSize: "14px",
    },
    loadingState: {
      textAlign: "center",
      padding: "60px",
      color: isDark ? "#64748b" : "#94a3b8",
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingState}>Loading feedbacks...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.96); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>

      <div style={styles.header}>
        <h2 style={styles.title}>Dev Feedback</h2>
        <button style={styles.addBtn} onClick={handleAddFeedback}>
          <PlusIcon /> New Feedback
        </button>
      </div>

      <div style={styles.statsRow}>
        <StatCard title="Pending" count={stats.pending} isDark={isDark} />
        <StatCard title="In Progress" count={stats.inProgress} isDark={isDark} />
        <StatCard title="Completed" count={stats.completed} isDark={isDark} />
        <StatCard title="Total" count={stats.total} isDark={isDark} />
      </div>

      <div style={styles.tabsRow}>
        <div style={styles.tabs}>
          {platforms.map(platform => (
            <TabButton
              key={platform}
              label={platform === "All" ? "All" : platform.replace('_', ' ')}
              isActive={selectedTab === platform}
              onClick={() => setSelectedTab(platform)}
              isDark={isDark}
            />
          ))}
        </div>

        <div style={styles.sortSection}>
          <span style={styles.sortLabel}>Sort by:</span>
          <div style={styles.sortButtons}>
            <button style={styles.sortBtn(sortBy === "priority")} onClick={() => setSortBy("priority")}>Priority</button>
            <button style={styles.sortBtn(sortBy === "id")} onClick={() => setSortBy("id")}>ID</button>
            <button style={styles.sortBtn(sortBy === "date")} onClick={() => setSortBy("date")}>Date</button>
          </div>
          <button style={styles.orderBtn} onClick={toggleSortOrder}>
            {sortOrder === "asc" ? "↑" : "↓"}
          </button>
        </div>
      </div>

      <div style={styles.feedbackList}>
        {filteredFeedbacks.length === 0 ? (
          <div style={styles.emptyState}>No feedback found</div>
        ) : (
          filteredFeedbacks.map(feedback => (
            <FeedbackCard
              key={feedback.id}
              feedback={feedback}
              isDark={isDark}
              onStatusChange={handleStatusChange}
            />
          ))
        )}
      </div>

      {isDialogOpen && (
        <AddFeedbackDialog
          isDark={isDark}
          onClose={() => {
            setIsDialogOpen(false);
            setEditingFeedback(null);
          }}
          onSave={handleSaveFeedback}
          editingFeedback={editingFeedback}
        />
      )}
    </div>
  );
};

export default DevFeedback;